import React, { useState, useEffect, useContext, useCallback } from "react";
import {
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import { useTheme, alpha } from "@material-ui/core/styles";
import ReceiptOutlinedIcon from "@material-ui/icons/ReceiptOutlined";
import moment from "moment";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import SubscriptionModal from "../../components/SubscriptionModal";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import useCompanies from "../../hooks/useCompanies";
import { AuthContext } from "../../context/Auth/AuthContext";
import {
  fmtBRL,
  useFinanceiroStyles,
  toneColor,
  StatusPill,
} from "./financeiroShared";

const getInvoiceStatus = (invoice) => {
  if (invoice.status === "paid") return "paid";
  const days = moment(invoice.dueDate).diff(moment().startOf("day"), "days");
  return days < 0 ? "overdue" : "pending";
};

const INVOICE_LABEL = { paid: "Paga", overdue: "Vencida", pending: "Em aberto" };

const FaturasPanel = ({ onSubtitle }) => {
  const classes = useFinanceiroStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { user } = useContext(AuthContext);
  const { finding } = useCompanies();

  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [companyDueDate, setCompanyDueDate] = useState(null);

  useEffect(() => {
    const load = async () => {
      if (!user?.companyId) return;
      try {
        const company = await finding(user.companyId);
        setCompanyDueDate(company.dueDate ? moment(company.dueDate) : null);
      } catch (err) {}
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.companyId]);

  // O endpoint /invoices/all retorna todas as faturas da empresa de uma vez.
  const loadInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/invoices/all");
      setInvoices(Array.isArray(data) ? data : []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  useEffect(() => {
    onSubtitle("Assinatura e faturas");
  }, [onSubtitle]);

  const handlePay = (invoice) => {
    setSelectedInvoice(invoice);
    setModalOpen(true);
  };

  const unpaid = invoices
    .filter((inv) => getInvoiceStatus(inv) !== "paid")
    .sort((a, b) => moment(a.dueDate).valueOf() - moment(b.dueDate).valueOf());
  const overdueCount = unpaid.filter((inv) => getInvoiceStatus(inv) === "overdue").length;
  const nextInvoice = unpaid[0];
  const paidTotal = invoices
    .filter((inv) => inv.status === "paid")
    .reduce((sum, inv) => sum + (inv.value || 0), 0);
  const paidCount = invoices.filter((inv) => inv.status === "paid").length;

  const days = companyDueDate ? companyDueDate.diff(moment().startOf("day"), "days") : null;
  const tone = days === null ? "grey" : days < 0 ? "red" : days <= 7 ? "amber" : "green";
  const color = toneColor(theme, tone);
  const headline =
    days === null
      ? "Licença sem data de vencimento"
      : days < 0
      ? `Sua licença venceu em ${companyDueDate.format("DD/MM/YYYY")}`
      : `Sua licença vale até ${companyDueDate.format("DD/MM/YYYY")}`;
  const caption =
    days === null
      ? ""
      : days < 0
      ? `Vencida há ${Math.abs(days)} dia${Math.abs(days) === 1 ? "" : "s"}.`
      : days === 0
      ? "Vence hoje."
      : `Vence em ${days} dia${days === 1 ? "" : "s"}.`;
  const pendingNote =
    unpaid.length === 0
      ? "Nenhuma fatura pendente."
      : overdueCount > 0
      ? `${unpaid.length} fatura${unpaid.length === 1 ? "" : "s"} pendente${unpaid.length === 1 ? "" : "s"}, ${overdueCount} vencida${overdueCount === 1 ? "" : "s"}. Regularize para evitar bloqueio.`
      : "Pague a próxima fatura para manter o acesso.";

  const payButton = (invoice) => (
    <Button
      size="small"
      variant="contained"
      color="primary"
      disableElevation
      style={{ textTransform: "none", fontWeight: 600, borderRadius: 8, height: 30 }}
      onClick={() => handlePay(invoice)}
    >
      Pagar
    </Button>
  );

  const statusPill = (invoice) => {
    const st = getInvoiceStatus(invoice);
    return <StatusPill status={st === "overdue" ? "overdue" : st} label={INVOICE_LABEL[st]} classes={classes} />;
  };

  return (
    <div className={classes.root}>
      <SubscriptionModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        Invoice={selectedInvoice}
        onPaid={loadInvoices}
      />

      <Paper
        variant="outlined"
        style={{
          display: "flex",
          alignItems: "center",
          gap: theme.spacing(2),
          flexWrap: "wrap",
          padding: theme.spacing(2, 2.5),
          borderRadius: 14,
          marginBottom: theme.spacing(1.75),
        }}
      >
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: "50%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            fontWeight: 700,
            lineHeight: 1.1,
            color,
            backgroundColor: alpha(color, 0.14),
          }}
          aria-hidden="true"
        >
          {days === null ? "—" : Math.abs(days)}
          <span style={{ fontSize: 10, fontWeight: 600 }}>{days === null ? "" : Math.abs(days) === 1 ? "dia" : "dias"}</span>
        </div>
        <div style={{ flex: "1 1 220px", minWidth: 0 }}>
          <Typography style={{ fontSize: 16, fontWeight: 700 }}>{headline}</Typography>
          <Typography variant="body2" className={classes.muted}>
            {[caption, pendingNote].filter(Boolean).join(" ")}
          </Typography>
        </div>
        {nextInvoice && (
          <Button
            variant="contained"
            color="primary"
            disableElevation
            fullWidth={isMobile}
            style={{ textTransform: "none", fontWeight: 600, borderRadius: 10, height: 40 }}
            onClick={() => handlePay(nextInvoice)}
          >
            Pagar {fmtBRL(nextInvoice.value)}
          </Button>
        )}
      </Paper>

      <Paper className={classes.mainPaper} variant="outlined">
        <div className={classes.tableWrapper}>
          {isMobile ? (
            <div>
              {!loading && invoices.length === 0 && (
                <div className={classes.emptyState}>
                  <ReceiptOutlinedIcon className={classes.emptyIcon} />
                  <Typography variant="body2">Nenhuma fatura encontrada.</Typography>
                </div>
              )}
              {invoices.map((invoice) => {
                const st = getInvoiceStatus(invoice);
                return (
                  <div key={invoice.id} className={`${classes.mobileRow} ${st === "overdue" ? classes.lateRow : ""}`}>
                    <div className={classes.mobileInfo}>
                      <div className={classes.mobileTitle}>{invoice.detail || "Fatura"}</div>
                      <div className={classes.subtle}>Vence {moment(invoice.dueDate).format("DD/MM/YYYY")}</div>
                    </div>
                    <div className={classes.mobileValue}>
                      <span className={classes.amount}>{fmtBRL(invoice.value)}</span>
                      {statusPill(invoice)}
                    </div>
                    {st !== "paid" && payButton(invoice)}
                  </div>
                );
              })}
              {loading && invoices.length === 0 && (
                <Typography align="center" className={classes.muted} style={{ padding: 24 }}>
                  Carregando...
                </Typography>
              )}
            </div>
          ) : (
            <Table stickyHeader size="small" aria-label="faturas">
              <TableHead>
                <TableRow>
                  <TableCell className={classes.headCell} style={{ paddingLeft: 20 }}>Fatura</TableCell>
                  <TableCell className={classes.headCell} align="right">Valor</TableCell>
                  <TableCell className={classes.headCell}>Vencimento</TableCell>
                  <TableCell className={classes.headCell}>Status</TableCell>
                  <TableCell className={classes.headCell} align="right" style={{ paddingRight: 20 }}>Ação</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoices.map((invoice) => {
                  const st = getInvoiceStatus(invoice);
                  return (
                    <TableRow key={invoice.id} className={`${classes.row} ${st === "overdue" ? classes.lateRow : ""}`}>
                      <TableCell style={{ paddingLeft: 20 }}>
                        <div style={{ fontWeight: 600 }}>{invoice.detail || "—"}</div>
                        <div className={classes.subtle}>Fatura #{invoice.id}</div>
                      </TableCell>
                      <TableCell align="right"><span className={classes.amount}>{fmtBRL(invoice.value)}</span></TableCell>
                      <TableCell>{moment(invoice.dueDate).format("DD/MM/YYYY")}</TableCell>
                      <TableCell>{statusPill(invoice)}</TableCell>
                      <TableCell align="right" style={{ paddingRight: 20 }}>
                        <div className={classes.actions}>{st !== "paid" && payButton(invoice)}</div>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {loading && invoices.length === 0 && <TableRowSkeleton columns={5} />}
                {!loading && invoices.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <div className={classes.emptyState}>
                        <ReceiptOutlinedIcon className={classes.emptyIcon} />
                        <Typography variant="body2">Nenhuma fatura encontrada.</Typography>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </div>
        {invoices.length > 0 && (
          <div className={classes.footer}>
            {invoices.length} fatura{invoices.length === 1 ? "" : "s"} · {paidCount} paga{paidCount === 1 ? "" : "s"} ({fmtBRL(paidTotal)})
          </div>
        )}
      </Paper>
    </div>
  );
};

export default FaturasPanel;
